// Cave84 — CellarTracker Import Wizard (Phase 1 Week 6, updated for real CT export format)
// Spec §4.13 F13.1–F13.3 + §8.1.
// Step 1: Upload CSV → parse client-side → show validation summary
// Step 2: Field mapping review (auto-detected from CT column headers)
// Step 3: Preview (first 10 rows) + confirmation
// Step 4: Result — shows CLI commands to run the actual import
//
// Tested against real CellarTracker export columns (18-column format):
//   Color, Category, Size, Currency, Value, Price, TotalQuantity, Quantity,
//   Pending, Vintage, Wine, Locale, Producer, Varietal, BeginConsume,
//   EndConsume, PScore, CScore
//
// The actual Firestore write uses firebase-admin (service account) and runs
// via the CLI script `npm run import:cellartracker`. This wizard is purely
// a UI preparation layer — it validates, maps, and guides the user through
// the import flow.

import { useState, useRef } from 'react';
import { Money } from '@/components/Money';

// ---------------------------------------------------------------------------
// CellarTracker known column headers — real 18-column export format
// ---------------------------------------------------------------------------

const CT_KNOWN_COLUMNS = [
  'Wine', 'Producer', 'Locale', 'Varietal',
  'Quantity', 'TotalQuantity', 'Pending',
  'Vintage',
  'Size', 'Currency', 'Price', 'Value',
  'BeginConsume', 'EndConsume',
  'Color', 'Category',
  'CScore', 'PScore',
  // Legacy column names (older CT exports — still recognised)
  'Begin Consume', 'End Consume', 'Bottle Size', 'CT Score', 'My Score',
  'Location', 'Bin', 'Notes', 'Type', 'Country', 'Region',
] as const;

type CTColumn = typeof CT_KNOWN_COLUMNS[number];

// Map CT column → Cave84 field path + human label
interface FieldMapping { field: string; note?: string }
const CAVE84_FIELDS: Record<string, FieldMapping> = {
  'Wine':          { field: 'wine.cuvee_name',           note: 'producer prefix stripped automatically' },
  'Producer':      { field: 'producer.name' },
  'Locale':        { field: 'appellation.hierarchy_path', note: 'country › region › subregion › appellation' },
  'Varietal':      { field: 'wine.grape_composition[0]',  note: 'primary grape (blends: best-effort)' },
  'Vintage':       { field: 'vintage.year',               note: '1001 → Non-Vintage (NV)' },
  'Quantity':      { field: 'bottle × N docs',            note: 'one Bottle doc per unit' },
  'TotalQuantity': { field: '— ignored',                  note: 'all-time count, not used' },
  'Pending':       { field: 'bottle.status',              note: '1 → virtual_pending, 0 → in_cellar' },
  'Size':          { field: 'bottle.format',              note: '375ml→half, 750ml→750ml, 1.5L→magnum' },
  'Currency':      { field: 'bottle.cost_currency' },
  'Price':         { field: 'bottle.cost_landed',         note: 'per-bottle purchase price' },
  'Value':         { field: 'market_price_history',       note: 'CT valuation → confidence: low' },
  'BeginConsume':  { field: 'vintage.drinking_window.from_year', note: '9999 → window unknown' },
  'EndConsume':    { field: 'vintage.drinking_window.to_year',   note: '9999 → window unknown' },
  'Color':         { field: 'wine.style (part)',          note: 'combined with Category' },
  'Category':      { field: 'wine.style (part)',          note: 'Dry/Sparkling/Sweet + Color → style enum' },
  'CScore':        { field: 'critic_score (community)',   note: 'source: cellartracker_import' },
  'PScore':        { field: 'critic_score (pro)',         note: 'rarely filled in CT exports' },
  // Legacy
  'Begin Consume': { field: 'vintage.drinking_window.from_year', note: 'legacy column name' },
  'End Consume':   { field: 'vintage.drinking_window.to_year',   note: 'legacy column name' },
  'Bottle Size':   { field: 'bottle.format',              note: 'legacy column name' },
  'CT Score':      { field: 'critic_score (community)',   note: 'legacy column name' },
  'Notes':         { field: 'bottle.notes' },
  'Location':      { field: '— (no physical data)',       note: 'CT bins → reassign via UI post-import' },
};

// ---------------------------------------------------------------------------
// Style mapping: CT Color + Category → Cave84 WineStyle
// ---------------------------------------------------------------------------

function mapStyle(color: string, category: string): string {
  const cat = category.toLowerCase();
  const col = color.toLowerCase();
  if (cat.includes('sparkling')) return 'sparkling';
  if (cat.includes('sweet') || cat.includes('dessert')) return 'sweet';
  if (col === 'red') return 'red';
  if (col === 'white') return 'white';
  if (col === 'rosé' || col === 'rose') return 'rose';
  return 'red';
}

function mapFormat(size: string): string {
  const s = size.toLowerCase().trim();
  if (s === '375ml') return 'half';
  if (s === '750ml') return '750ml';
  if (s === '1.5l' || s === '1500ml') return 'magnum';
  if (s === '2.25l' || s === '3l') return 'double_magnum';
  if (s === '500ml') return 'other';
  return 'other';
}

// ---------------------------------------------------------------------------
// CSV parser (handles quoted fields, UTF-8)
// ---------------------------------------------------------------------------

/**
 * CSV files carry no encoding metadata. CellarTracker exports are windows-1252
 * (verified against a real export: "Ch\xE2teau Rauzan-S\xE9gla", zero UTF-8
 * sequences), while our own export and anything re-saved from modern Excel is
 * UTF-8 with a BOM. Reading either one with a hardcoded label corrupts the
 * other, so sniff instead:
 *   1. UTF-8 BOM  -> UTF-8, unambiguous.
 *   2. strict UTF-8 -> if it decodes without an invalid sequence it IS UTF-8;
 *      windows-1252 text with accents almost always fails here, because cp1252
 *      accented bytes (0xC0-0xFF) are UTF-8 lead bytes not followed by valid
 *      continuation bytes.
 *   3. otherwise    -> windows-1252.
 * Pure-ASCII files decode identically either way, so the order is safe.
 */
export function decodeCsvBuffer(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('utf-8').decode(bytes.subarray(3));
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

function parseCsv(text: string): { headers: string[]; rows: Record<string, string>[] } {
  // Strip a leading UTF-8 BOM before anything else — Excel, CellarTracker and
  // our own export all emit one, and left in place it corrupts the first header
  // name ("\uFEFFVintage"), silently breaking column detection.
  const lines = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
  if (lines.length < 2) return { headers: [], rows: [] };

  function splitLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { current += '"'; i++; }
        else { inQuote = !inQuote; }
      } else if (ch === ',' && !inQuote) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  }

  const headers = splitLine(lines[0] ?? '');
  const rows: Record<string, string>[] = [];
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i]?.trim()) continue;
    const cells = splitLine(lines[i] ?? '');
    const row: Record<string, string> = {};
    headers.forEach((h, idx) => { row[h] = cells[idx] ?? ''; });
    rows.push(row);
  }
  return { headers, rows };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

interface SampleWine {
  label: string;
  style: string;
  format: string;
  qty: number;
}

interface ValidationResult {
  totalRows: number;
  validRows: number;
  totalBottles: number;
  pendingBottles: number;
  warnings: string[];
  errors: string[];
  recognizedColumns: string[];
  unknownColumns: string[];
  sampleWines: SampleWine[];
  vintageRange: [number, number] | null;
  nvCount: number;
  unknownWindowCount: number;
  cScoreFillPct: number;
  styleBreakdown: Record<string, number>;
  formatBreakdown: Record<string, number>;
}

function validateCsv(
  headers: string[],
  rows: Record<string, string>[]
): ValidationResult {
  const recognizedColumns = headers.filter(h =>
    CT_KNOWN_COLUMNS.includes(h as CTColumn)
  );
  const unknownColumns = headers.filter(h =>
    !CT_KNOWN_COLUMNS.includes(h as CTColumn)
  );

  const warnings: string[] = [];
  const errors: string[] = [];

  if (!headers.includes('Wine'))     errors.push('Missing required column: Wine');
  if (!headers.includes('Vintage'))  errors.push('Missing required column: Vintage');
  if (!headers.includes('Producer')) warnings.push('No Producer column — producer inferred from Wine name');
  if (!headers.includes('Quantity')) warnings.push('No Quantity column — will default to 1 per row');
  if (!headers.includes('Price'))    warnings.push('No Price column — cost_landed will be 0');

  if (headers.includes('Begin Consume') && !headers.includes('BeginConsume')) {
    warnings.push('Older CT export format detected ("Begin Consume" with space). Import will still work.');
  }

  let validRows = 0;
  let totalBottles = 0;
  let pendingBottles = 0;
  let nvCount = 0;
  let unknownWindowCount = 0;
  let cScoreFilled = 0;
  const vintages: number[] = [];
  const sampleWines: SampleWine[] = [];
  const styleBreakdown: Record<string, number> = {};
  const formatBreakdown: Record<string, number> = {};

  for (const row of rows) {
    const wine = row['Wine']?.trim();
    if (!wine) continue;

    const vintageRaw = parseInt(row['Vintage'] ?? '');
    const qty = parseInt(row['Quantity'] ?? '1') || 1;
    const pending = parseInt(row['Pending'] ?? '0') || 0;
    const style = mapStyle(row['Color'] ?? '', row['Category'] ?? '');
    const format = mapFormat(row['Size'] ?? '750ml');
    const beginConsume = row['BeginConsume'] ?? row['Begin Consume'] ?? '';
    const endConsume = row['EndConsume'] ?? row['End Consume'] ?? '';

    validRows++;
    totalBottles += qty;
    pendingBottles += pending;

    if (vintageRaw === 1001) {
      nvCount++;
    } else if (!isNaN(vintageRaw) && vintageRaw > 1800 && vintageRaw <= new Date().getFullYear()) {
      vintages.push(vintageRaw);
    }

    if (beginConsume === '9999' || endConsume === '9999') unknownWindowCount++;
    if ((row['CScore'] ?? '').trim()) cScoreFilled++;

    styleBreakdown[style] = (styleBreakdown[style] ?? 0) + qty;
    formatBreakdown[format] = (formatBreakdown[format] ?? 0) + qty;

    if (sampleWines.length < 6) {
      const vintLabel = vintageRaw === 1001 ? 'NV' : (vintageRaw > 1800 ? String(vintageRaw) : '?');
      sampleWines.push({ label: `${vintLabel} ${wine}`, style, format, qty });
    }
  }

  if (validRows === 0) errors.push('No valid rows found');

  const vintageRange: [number, number] | null = vintages.length
    ? [Math.min(...vintages), Math.max(...vintages)]
    : null;

  const cScoreFillPct = validRows > 0 ? Math.round((cScoreFilled / validRows) * 100) : 0;

  return {
    totalRows: rows.length,
    validRows,
    totalBottles,
    pendingBottles,
    warnings,
    errors,
    recognizedColumns,
    unknownColumns,
    sampleWines,
    vintageRange,
    nvCount,
    unknownWindowCount,
    cScoreFillPct,
    styleBreakdown,
    formatBreakdown,
  };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type WizardStep = 'upload' | 'mapping' | 'preview' | 'done';

interface ParsedData {
  headers: string[];
  rows: Record<string, string>[];
  validation: ValidationResult;
  fileName: string;
}

// ---------------------------------------------------------------------------
// Step 1 — Upload
// ---------------------------------------------------------------------------

function UploadStep({ onParsed }: { onParsed: (data: ParsedData) => void }) {
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function processFile(file: File) {
    if (!file.name.endsWith('.csv')) {
      setError('Please upload a .csv file exported from CellarTracker.');
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      // Read raw bytes, not text: readAsText needs an encoding label up front
      // and CellarTracker (cp1252) vs Excel (UTF-8 BOM) disagree. See decodeCsvBuffer.
      const buf = e.target?.result as ArrayBuffer;
      const text = decodeCsvBuffer(buf);
      const { headers, rows } = parseCsv(text);
      const validation = validateCsv(headers, rows);
      onParsed({ headers, rows, validation, fileName: file.name });
    };
    reader.onerror = () => setError('Failed to read file.');
    reader.readAsArrayBuffer(file);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-text-primary mb-1">Step 1 — Upload CSV</h2>
        <p className="text-xs text-text-muted">
          From CellarTracker: <span className="font-mono">My Cellar &rarr; Export &rarr; Export Inventory (CSV)</span>
        </p>
      </div>

      <div
        role="button"
        tabIndex={0}
        onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
        className={`border-2 border-dashed rounded-xl px-8 py-12 text-center cursor-pointer transition-colors ${
          isDragging
            ? 'border-gold bg-gold/5'
            : 'border-border-medium hover:border-border-strong bg-surface-2'
        }`}
      >
        <p className="text-3xl mb-3">📂</p>
        <p className="text-sm font-medium text-text-primary">Drop CSV here or click to browse</p>
        <p className="text-xs text-text-muted mt-1">CellarTracker export CSV, any encoding</p>
        <input ref={inputRef} type="file" accept=".csv" className="hidden" onChange={handleChange} />
      </div>

      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2 — Field Mapping
// ---------------------------------------------------------------------------

function MappingStep({
  data,
  onNext,
  onBack,
}: {
  data: ParsedData;
  onNext: () => void;
  onBack: () => void;
}) {
  const { validation } = data;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-text-primary mb-1">Step 2 — Field Mapping</h2>
        <p className="text-xs text-text-muted">
          Cave84 auto-detected {validation.recognizedColumns.length} of {data.headers.length} columns.
        </p>
      </div>

      {validation.errors.length > 0 && (
        <div className="bg-red-950/30 border border-red-800 rounded-lg p-3 space-y-1">
          {validation.errors.map(e => (
            <p key={e} className="text-sm text-red-400 flex items-start gap-2">
              <span>&#x2717;</span> {e}
            </p>
          ))}
        </div>
      )}

      {validation.warnings.length > 0 && (
        <div className="bg-amber-950/20 border border-amber-800 rounded-lg p-3 space-y-1">
          {validation.warnings.map(w => (
            <p key={w} className="text-sm text-amber-300 flex items-start gap-2">
              <span>&#x26A0;</span> {w}
            </p>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-border-medium overflow-hidden">
        <div className="grid grid-cols-3 bg-surface-3 px-3 py-2 text-xs font-medium text-text-muted uppercase tracking-wide">
          <span>CT Column</span>
          <span>Cave84 Field</span>
          <span>Note</span>
        </div>
        <div className="divide-y divide-border-subtle">
          {data.headers.map(h => {
            const mapped = CAVE84_FIELDS[h];
            const isIgnored = mapped?.field.startsWith('—');
            return (
              <div key={h} className="grid grid-cols-3 px-3 py-2 bg-surface-1 gap-2">
                <span className="font-mono text-xs text-text-primary self-center">{h}</span>
                <span className={`text-xs font-mono self-center ${
                  !mapped ? 'text-text-muted italic' :
                  isIgnored ? 'text-text-muted' :
                  'text-emerald-400'
                }`}>
                  {mapped?.field ?? '— (unknown)'}
                </span>
                <span className="text-xs text-text-muted self-center leading-tight">
                  {mapped?.note ?? ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-surface-2 border border-amber-800/40 rounded-lg p-3">
        <p className="text-xs font-medium text-amber-300 mb-1">No location data in CT export</p>
        <p className="text-xs text-text-muted">
          CellarTracker does not export physical fridge/shelf assignments.
          All imported bottles default to <span className="text-text-primary font-medium">Home cellar</span>.
          Re-assign via the Cellar UI after import.
        </p>
      </div>

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack}
          className="px-4 py-2 rounded-md border border-border-medium bg-surface-2 text-text-primary text-sm hover:bg-surface-3">
          &larr; Back
        </button>
        <button type="button" onClick={onNext}
          disabled={validation.errors.length > 0}
          className="px-5 py-2 rounded-md bg-gold text-black font-semibold text-sm hover:bg-gold/90 disabled:opacity-40">
          Next &rarr;
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3 — Preview
// ---------------------------------------------------------------------------

const STYLE_COLOR: Record<string, string> = {
  red: '#C49255',
  white: '#C49255',
  sparkling: '#a8c5da',
  sweet: '#d4a8c5',
  rose: '#e8a0a0',
};

function PreviewStep({
  data,
  onNext,
  onBack,
}: {
  data: ParsedData;
  onNext: () => void;
  onBack: () => void;
}) {
  const { validation } = data;
  const preview = data.rows.slice(0, 10);

  const statItems = [
    { label: 'Wine entries', value: String(validation.validRows) },
    { label: 'Bottles total', value: String(validation.totalBottles) },
    { label: 'Virtual pending', value: String(validation.pendingBottles) },
    {
      label: 'Vintage range',
      value: validation.vintageRange
        ? `${validation.vintageRange[0]}–${validation.vintageRange[1]}`
        : validation.nvCount > 0 ? 'NV only' : '—',
    },
    { label: 'NV wines', value: String(validation.nvCount) },
    { label: 'CScore fill', value: `${validation.cScoreFillPct}%` },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-text-primary mb-1">Step 3 — Preview &amp; Confirm</h2>
        <p className="text-xs text-text-muted">Review the data before running the import.</p>
      </div>

      <div className="grid grid-cols-2 gap-px bg-border-subtle rounded-lg overflow-hidden">
        {statItems.map(item => (
          <div key={item.label} className="bg-surface-1 px-3 py-2.5 text-center">
            <p className="text-base font-bold text-text-primary tabular-nums">{item.value}</p>
            <p className="text-xs text-text-muted">{item.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-surface-2 rounded-lg p-3">
        <p className="text-xs font-medium text-text-muted mb-2">Style breakdown (bottles)</p>
        <div className="flex flex-wrap gap-2">
          {Object.entries(validation.styleBreakdown).map(([style, count]) => (
            <span key={style} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-surface-3 border border-border-medium text-xs">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: STYLE_COLOR[style] ?? '#666' }}
              />
              <span className="text-text-secondary capitalize">{style}</span>
              <span className="text-text-muted">{count}</span>
            </span>
          ))}
        </div>
      </div>

      {validation.unknownWindowCount > 0 && (
        <p className="text-xs text-amber-300">
          &#x26A0; {validation.unknownWindowCount} wines have no drinking window in CT (BeginConsume=9999).
          Drinking windows will be set by Wine Analysis generation.
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-border-medium">
        <table className="text-xs w-full">
          <thead className="bg-surface-3">
            <tr>
              {(["Vint.", "Wine", "Producer", "Qty", "Format", "Price", "Score"] as const).map(col => (
                <th key={col} className="px-2 py-2 text-left font-medium text-text-muted whitespace-nowrap">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {preview.map((row, idx) => {
              const style = mapStyle(row["Color"] ?? "", row["Category"] ?? "");
              const fmt = mapFormat(row["Size"] ?? "750ml");
              const vintRaw = parseInt(row["Vintage"] ?? "");
              const vintLabel = vintRaw === 1001 ? "NV" : String(vintRaw || "?");
              const dotColor = STYLE_COLOR[style] ?? "#666";
              return (
                <tr key={idx} className="bg-surface-1 hover:bg-surface-2">
                  <td className="px-2 py-2 tabular-nums text-text-muted whitespace-nowrap">{vintLabel}</td>
                  <td className="px-2 py-2 text-text-primary max-w-[160px] truncate">
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle"
                      style={{ backgroundColor: dotColor }}
                    />
                    {row["Wine"] ?? "—"}
                  </td>
                  <td className="px-2 py-2 text-text-muted max-w-[110px] truncate">{row["Producer"] ?? "—"}</td>
                  <td className="px-2 py-2 tabular-nums text-text-secondary">{row["Quantity"] ?? "1"}</td>
                  <td className="px-2 py-2 text-text-muted whitespace-nowrap">{fmt}</td>
                  <td className="px-2 py-2 tabular-nums text-text-secondary">
                    {(() => {
                      const parsed = row["Price"] ? parseFloat(row["Price"]) : NaN;
                      return Number.isFinite(parsed)
                        ? <Money value={parsed} currency="EUR" minWidthCh={6} />
                        : row["Price"] || "—";
                    })()}
                  </td>
                  <td className="px-2 py-2 tabular-nums text-emerald-400">{row["CScore"] || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {data.rows.length > 10 && (
          <p className="px-3 py-2 text-xs text-text-muted bg-surface-2 border-t border-border-subtle">
            &hellip;and {data.rows.length - 10} more rows ({validation.totalBottles} total bottles)
          </p>
        )}
      </div>

      <div className="flex justify-between pt-2">
        <button type="button" onClick={onBack}
          className="px-4 py-2 rounded-md border border-border-medium bg-surface-2 text-text-primary text-sm hover:bg-surface-3">
          &larr; Back
        </button>
        <button type="button" onClick={onNext}
          className="px-5 py-2 rounded-md bg-gold text-black font-semibold text-sm hover:bg-gold/90">
          Run Import &rarr;
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4 — Done (CLI commands)
// ---------------------------------------------------------------------------

function DoneStep({ data }: { data: ParsedData }) {
  const [copied, setCopied] = useState<string | null>(null);

  function copy(text: string, key: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  const csvPath = `C:\\Users\\MotiMaaravi\\Documents\\Cave84\\${data.fileName}`;
  const dryRunCmd =
    `cd C:\\Users\\MotiMaaravi\\Documents\\Cave84\n` +
    `npm run import:cellartracker -- --csv "${data.fileName}" --dry-run`;
  const importCmd =
    `$env:GOOGLE_APPLICATION_CREDENTIALS = "C:\\Users\\MotiMaaravi\\Documents\\credentials\\cave84-service-account.json"\n` +
    `npm run import:cellartracker -- --csv "${data.fileName}" --skip-existing`;
  const queueCmd = `npm run seed-generation-queue`;

  type Block = { step: string; label: string; cmd: string; key: string; color: string };
  const blocks: Block[] = [
    { step: "2", label: "Dry run first (PowerShell)", cmd: dryRunCmd, key: "dry",    color: "text-amber-300" },
    { step: "3", label: "Run the import (PowerShell)", cmd: importCmd, key: "import", color: "text-emerald-300" },
    { step: "4", label: "Queue new wines for analysis", cmd: queueCmd, key: "queue",  color: "text-violet-300" },
  ];

  return (
    <div className="space-y-5">
      <div className="text-center py-4">
        <p className="text-4xl mb-3">&#x2705;</p>
        <h2 className="text-base font-semibold text-text-primary">Validation passed</h2>
        <p className="text-sm text-text-muted mt-1">
          {data.validation.validRows} wine entries &middot; {data.validation.totalBottles} bottles &middot; {data.validation.cScoreFillPct}% CScore fill
        </p>
      </div>

      <div className="bg-emerald-950/30 border border-emerald-700/60 rounded-lg p-3">
        <p className="text-xs text-emerald-400 font-medium mb-0.5">Phase 0.5 already imported</p>
        <p className="text-xs text-text-muted">
          Cave84 Firestore already contains 518 bottles from the original CellarTracker import.
          Use <span className="font-mono text-text-primary">--skip-existing</span> to import only new bottles.
          The dry-run shows exactly what is new vs. already present.
        </p>
      </div>

      <div className="bg-surface-2 border border-border-medium rounded-lg p-4 space-y-5">
        <div>
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1.5">
            1. Ensure CSV is at repo root
          </p>
          <div className="flex items-center gap-2">
            <code className="text-xs font-mono text-text-primary bg-surface-0 border border-border-medium rounded px-2 py-1 flex-1 min-w-0 truncate">
              {csvPath}
            </code>
            <button
              type="button"
              onClick={() => copy(csvPath, "path")}
              className="px-2 py-1 rounded text-xs border border-border-medium bg-surface-3 text-text-muted hover:text-text-primary flex-shrink-0"
            >
              {copied === "path" ? "✓" : "Copy"}
            </button>
          </div>
        </div>

        {blocks.map(block => (
          <div key={block.key}>
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-1.5">
              {block.step}. {block.label}
            </p>
            <div className="relative">
              <pre className={`bg-surface-0 border border-border-medium rounded p-3 text-xs font-mono ${block.color} overflow-x-auto whitespace-pre-wrap break-all pr-14`}>
                {block.cmd}
              </pre>
              <button
                type="button"
                onClick={() => copy(block.cmd, block.key)}
                className="absolute top-2 right-2 px-2 py-1 rounded text-xs border border-border-medium bg-surface-2 text-text-muted hover:text-text-primary"
              >
                {copied === block.key ? "✓" : "Copy"}
              </button>
            </div>
            {block.key === "queue" && (
              <p className="text-xs text-text-muted mt-1.5">
                <span className="font-mono">seed-generation-queue</span> automatically skips wines that already have analyses.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ImportWizardPage() {
  const [step, setStep] = useState<WizardStep>("upload");
  const [parsedData, setParsedData] = useState<ParsedData | null>(null);

  const STEP_LABELS: Record<WizardStep, string> = {
    upload: "Upload CSV",
    mapping: "Field Mapping",
    preview: "Preview",
    done: "Run Import",
  };

  const STEPS: WizardStep[] = ["upload", "mapping", "preview", "done"];
  const stepIndex = STEPS.indexOf(step);

  function handleParsed(d: ParsedData) {
    setParsedData(d);
    setStep("mapping");
  }

  return (
    <div className="min-h-screen bg-surface-0">
      <div className="sticky top-0 z-10 bg-surface-1 border-b border-border-medium px-4 py-3 flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-base font-semibold text-text-primary">Import from CellarTracker</h1>
          <p className="text-xs text-text-muted">{STEP_LABELS[step]}</p>
        </div>
        <div className="flex gap-1.5">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === stepIndex ? "bg-gold" : i < stepIndex ? "bg-gold/40" : "bg-border-medium"
              }`}
            />
          ))}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {step === "upload" && <UploadStep onParsed={handleParsed} />}
        {step === "mapping" && parsedData && (
          <MappingStep
            data={parsedData}
            onNext={() => setStep("preview")}
            onBack={() => setStep("upload")}
          />
        )}
        {step === "preview" && parsedData && (
          <PreviewStep
            data={parsedData}
            onNext={() => setStep("done")}
            onBack={() => setStep("mapping")}
          />
        )}
        {step === "done" && parsedData && <DoneStep data={parsedData} />}
      </div>
    </div>
  );
}
